import React, { useEffect, useState, useCallback } from 'react';
import ProductPicker from '../components/ProductPicker';
import ContactPicker from '../components/ContactPicker';
import { Search, Plus, CheckCircle, FileText, DollarSign } from 'lucide-react';
import { purchaseApi } from '../api/purchase';
import InvoicePicker from '../components/InvoicePicker';
import GrnDetail from './GrnDetail';
import PurchaseInvoiceDetail from './PurchaseInvoiceDetail';
import SupplierPaymentDetail from './SupplierPaymentDetail';
import { contactsApi } from '../api/contacts';
import { productsApi } from '../api/products';
import { coaApi } from '../api/coa';
import { useToast } from '../hooks/useToast';

interface GRN { id:number; grn_no:string; supplier_name:string; status:string; status_color:string; created_at:string; grn_date:string; items_total:number; total_amount:number; landed_cost_total:number; contact_id:number; branch_id:number; }
interface Contact { id:number; name:string; ap_balance:number; }
const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', purple:'badge-purple', red:'badge-red' };

const Purchase: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab]               = useState<'grns'|'invoices'|'payments'>('grns');
  const [grns, setGrns]             = useState<GRN[]>([]);
  const [invGrns, setInvGrns] = useState<any[]>([]);
  const [invoices, setInvoices]     = useState<any[]>([]);
  const [payments, setPayments]     = useState<any[]>([]);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInvForm, setShowInvForm]   = useState(false);
  const [showPayForm, setShowPayForm]   = useState(false);
  const [importContact, setImportContact] = useState('');
  const [importBranch, setImportBranch] = useState('');
  const [saving, setSaving]         = useState(false);
  const [selectedGrn, setSelectedGrn] = useState<number|null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<number|null>(null);
  const [selectedPayment, setSelectedPayment] = useState<number|null>(null);
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [products, setProducts]     = useState<any[]>([]);
  const [locations, setLocations]   = useState<any[]>([]);
  const [branches, setBranches]     = useState<any[]>([]);
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);
  const [form, setForm]     = useState({ contact_id:'', branch_id:'', notes:'', grn_date: new Date().toISOString().split('T')[0] });
  const [invForm, setInvForm] = useState({ contact_id:'', branch_id:'', grn_id:'', supplier_invoice_no:'', invoice_date:new Date().toISOString().split('T')[0], due_date:'', invoice_amount:'' });
  const [payForm, setPayForm] = useState({ contact_id:'', branch_id:'', purchase_invoice_id:'', payment_ref:'', notes:'', payment_date: new Date().toISOString().split('T')[0] });
  const [paySplits, setPaySplits] = useState([{ account_id:'', amount:'' }]);
  const addPaySplit = () => setPaySplits(s => [...s, { account_id:'', amount:'' }]);
  const removePaySplit = (i:number) => setPaySplits(s => s.filter((_,idx)=>idx!==i));
  const updatePaySplit = (i:number, f:string, v:string) => setPaySplits(s => s.map((sp,idx)=>idx===i?{...sp,[f]:v}:sp));
  const payTotal = paySplits.reduce((s,sp)=>s+Number(sp.amount||0),0);
  const [lines, setLines]   = useState<any[]>([{ product_id:'', location_id:'', qty_received:'1', unit_cost:'0', landed_per_unit:'', batch_no:'', expiry_date:'', has_expiry:false }]);

  const loadMeta = () => {
    contactsApi.list().then(r => setContacts(r.data.data));
    productsApi.list().then(r => setProducts(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
    purchaseApi.branches().then(r => setBranches(r.data.data));
    coaApi.list().then(r => setCashAccounts(r.data.data.filter((a:any) => a.type_name === 'Asset' && a.subtype_name === 'Current Asset')));
    purchaseApi.grns({ uninvoiced: 1, search:'', limit: 500 }).then(r => setInvGrns(r.data.data || []));
  };

  const load = useCallback(() => {
    setLoading(true);
    if (tab === 'grns') {
      purchaseApi.grns({ search }).then(res => { setGrns(res.data.data); setTotal(res.data.total); setLoading(false); });
    } else if (tab === 'invoices') {
      purchaseApi.supplierInvoices({ search }).then(res => { setInvoices(res.data.data); setTotal(res.data.total); setLoading(false); });
    } else {
      purchaseApi.supplierPayments().then(res => { setPayments(res.data.data); setTotal(res.data.data.length); setLoading(false); });
    }
  }, [search, tab]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const addLine    = () => setLines(l => [...l, { product_id:'', location_id:'', qty_received:'1', unit_cost:'0', landed_per_unit:'', batch_no:'', expiry_date:'', has_expiry:false }]);
  const removeLine = (i:number) => setLines(l => l.filter((_,idx) => idx !== i));
  const updateLine = (i:number, field:string, val:string) => setLines(l => l.map((line,idx) => idx===i ? {...line,[field]:val} : line));
  const updateLineFields = (i:number, fields: Record<string,any>) => setLines(l => l.map((line,idx) => idx===i ? {...line,...fields} : line));

  const handleCreate = async () => {
    if (!form.contact_id || !form.branch_id) { showToast('Supplier and branch required', 'error'); return; }
    const items = lines.filter(l => l.product_id && l.location_id).map(l => ({ product_id:Number(l.product_id), location_id:Number(l.location_id), qty_received:Number(l.qty_received), unit_cost:Number(l.unit_cost), landed_per_unit:Number(l.landed_per_unit||0), batch_no:l.batch_no||null, expiry_date:l.expiry_date||null }));
    setSaving(true);
    try {
      const res = await purchaseApi.createGrn({ ...form, contact_id:Number(form.contact_id), branch_id:Number(form.branch_id), items });
      showToast('GRN created!'); setShowForm(false);
      setLines([{ product_id:'', location_id:'', qty_received:'1', unit_cost:'0' }]);
      setSelectedGrn(res.data.data.id);
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleQuickGrn = async () => {
    if (!importContact) { showToast('Select a supplier', 'error'); return; }
    setSaving(true);
    try {
      const res = await purchaseApi.createGrn({ contact_id:Number(importContact), branch_id:Number(importBranch)||0, notes:'', items:[] });
      showToast('GRN created'); setShowImportModal(false); setImportContact(''); setSelectedGrn(res.data.data.id);
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleConfirm = async (id:number, grnNo:string, e:React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Confirm GRN ${grnNo}?`)) return;
    try { const res = await purchaseApi.confirmGrn(id); showToast(res.data.message); load(); }
    catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleInvoice = async () => {
    if (!invForm.contact_id || !invForm.invoice_amount) { showToast('Supplier and amount required', 'error'); return; }
    setSaving(true);
    try {
      const res = await purchaseApi.createSupplierInvoice({ ...invForm, contact_id:Number(invForm.contact_id), branch_id:Number(invForm.branch_id), grn_id:invForm.grn_id?Number(invForm.grn_id):null, invoice_amount:Number(invForm.invoice_amount) });
      showToast(res.data.message); setShowInvForm(false); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handlePayment = async () => {
    if (!payForm.contact_id || !payForm.branch_id) { showToast('Supplier and branch required', 'error'); return; }
    if (paySplits.some(s => !s.account_id || !s.amount)) { showToast('Fill all split accounts and amounts', 'error'); return; }
    setSaving(true);
    try {
      const splits = paySplits.map(s => ({ account_id:Number(s.account_id), amount:Number(s.amount) }));
      const res = await purchaseApi.createSupplierPayment({ ...payForm, contact_id:Number(payForm.contact_id), branch_id:Number(payForm.branch_id), purchase_invoice_id:payForm.purchase_invoice_id?Number(payForm.purchase_invoice_id):null, splits });
      showToast(res.data.message); setShowPayForm(false); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const grandTotal = lines.reduce((s,l) => s + (Number(l.qty_received)*Number(l.unit_cost)), 0);

  if (selectedPayment) return <SupplierPaymentDetail paymentId={selectedPayment} onBack={() => { setSelectedPayment(null); load(); }} />;
  if (selectedInvoice) return <PurchaseInvoiceDetail invoiceId={selectedInvoice} onBack={() => { setSelectedInvoice(null); load(); }} />;
  if (selectedGrn) return <GrnDetail grnId={selectedGrn} onBack={() => { setSelectedGrn(null); load(); }} onConfirmed={() => { setSelectedGrn(null); load(); }} />;

  if (selectedGrn) return <GrnDetail grnId={selectedGrn} onBack={() => setSelectedGrn(null)} onConfirmed={() => { setSelectedGrn(null); load(); }} />;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Purchase</h1>
        <div style={{ display:'flex', gap:8 }}>
          {tab === 'grns' && <>
            <button className="btn btn-secondary" onClick={() => { setShowForm(!showForm); setShowImportModal(false); }}><Plus size={13} /> Manual GRN</button>
            <button className="btn btn-primary" onClick={() => { setShowImportModal(!showImportModal); setShowForm(false); }}>Import GRN</button>
          </>}
          {tab === 'invoices' && <button className="btn btn-primary" onClick={() => setShowInvForm(!showInvForm)}><FileText size={13} /> New Invoice</button>}
          {tab === 'payments' && <button className="btn btn-success" onClick={() => setShowPayForm(!showPayForm)}><DollarSign size={13} /> Pay Supplier</button>}
        </div>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:'2px solid var(--bdr)', paddingBottom:0 }}>
        {(['grns','invoices','payments'] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setSearch(''); }} style={{ padding:'8px 16px', fontSize:12, fontWeight:600, border:'none', background:'none', cursor:'pointer', borderBottom: tab===t ? '2px solid var(--blue)' : '2px solid transparent', color: tab===t ? 'var(--blue)' : 'var(--tx3)', marginBottom:'-2px', textTransform:'capitalize' }}>
            {t === 'grns' ? 'GRNs' : t === 'invoices' ? 'Supplier Invoices' : 'Payments'}
          </button>
        ))}
      </div>

      {showImportModal && tab==='grns' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New GRN — CSV Import</span><button className="btn btn-secondary btn-sm" onClick={() => setShowImportModal(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-group" style={{ maxWidth:320 }}>
              <label className="form-label">Supplier *</label>
              <ContactPicker value={importContact} placeholder="Type to search supplier" onSelect={(c:any) => setImportContact(String(c.id))} />
            </div>
            <div className="form-group" style={{ maxWidth:320, marginTop:12 }}>
              <label className="form-label">Branch *</label>
              <select className="form-control" value={importBranch} onChange={e => setImportBranch(e.target.value)}>
                <option value="">Select branch</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleQuickGrn} disabled={saving || !importContact || !importBranch}>{saving ? 'Creating...' : 'Create GRN and Go to Import'}</button>
          </div>
        </div>
      )}

      {showForm && tab==='grns' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Manual GRN</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Supplier *</label>
                <ContactPicker value={form.contact_id} placeholder="Type to search supplier" onSelect={(c:any) => setForm(f=>({...f,contact_id:String(c.id)}))} />
              </div>
              <div className="form-group"><label className="form-label">Date *</label>
                <input type="date" className="form-control" value={form.grn_date} max={new Date().toISOString().split('T')[0]} onChange={e => setForm(f=>({...f,grn_date:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><input className="form-control" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} /></div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th><th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Landed / unit</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                <th></th>
              </tr></thead>
              <tbody>
                {lines.map((line,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'6px 4px' }}>
                      <ProductPicker
                        value={line.product_id}
                        fontSize={12}
                        onSelect={(p:any) => {
                          updateLineFields(i, { product_id: String(p.id), has_expiry: !!p.has_expiry });
                        }}
                      />
                      {line.has_expiry && (
                        <div style={{ display:'flex', gap:4, marginTop:4 }}>
                          <input className="form-control" style={{ fontSize:11, flex:1 }} placeholder="Batch no"
                            value={line.batch_no||''} onChange={e => updateLine(i,'batch_no',e.target.value)} />
                          <input type="date" className="form-control" style={{ fontSize:11, width:130 }} title="Expiry date"
                            value={line.expiry_date||''} onChange={e => updateLine(i,'expiry_date',e.target.value)} />
                        </div>
                      )}
                    </td>
                    <td style={{ padding:'6px 4px' }}>
                      <select className="form-control" value={line.location_id} onChange={e => updateLine(i,'location_id',e.target.value)} style={{ fontSize:12 }}>
                        <option value="">Select location</option>
                        {locations.filter((l:any) => !form.branch_id || String(l.branch_id) === String(form.branch_id)).map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'6px 4px', width:80 }}><input className="form-control mono" type="number" value={line.qty_received} onChange={e => updateLine(i,'qty_received',e.target.value)} style={{ textAlign:'right' }} /></td>
                    <td style={{ padding:'6px 4px', width:100 }}><input className="form-control mono" type="number" value={line.unit_cost} onChange={e => updateLine(i,'unit_cost',e.target.value)} style={{ textAlign:'right' }} /></td><td style={{ padding:'6px 4px', width:90 }}><input className="form-control mono" type="number" placeholder="0" value={line.landed_per_unit} onChange={e => updateLine(i,'landed_per_unit',e.target.value)} style={{ textAlign:'right' }} /></td>
                    <td style={{ padding:'6px 4px', width:100 }}>
                      <input
                        className="form-control mono"
                        type="text"
                        inputMode="decimal"
                        value={line.total_override !== undefined ? line.total_override : (Number(line.qty_received)*Number(line.unit_cost)).toFixed(2)}
                        onChange={e => {
                          const raw = e.target.value;
                          const newTotal = Number(raw);
                          const qty = Number(line.qty_received);
                          const fields: Record<string,any> = { total_override: raw };
                          if (!isNaN(newTotal) && qty > 0) {
                            fields.unit_cost = (newTotal / qty).toFixed(6);
                          }
                          updateLineFields(i, fields);
                        }}
                        onBlur={() => updateLineFields(i, { total_override: undefined })}
                        style={{ textAlign:'right', fontWeight:600 }}
                      />
                    </td>
                    <td><button onClick={() => removeLine(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:18 }}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={addLine}>+ Add Line</button>
              <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:14 }}>Total: {grandTotal.toFixed(2)}</div>
            </div>
            <div style={{ marginTop:14 }}>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Save GRN'}</button>
            </div>
          </div>
        </div>
      )}

      {showInvForm && tab==='invoices' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Supplier Invoice</span><button className="btn btn-secondary btn-sm" onClick={() => setShowInvForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Supplier *</label>
                <ContactPicker value={invForm.contact_id} placeholder="Type to search supplier" onSelect={(c:any) => setInvForm(f=>({...f,contact_id:String(c.id)}))} />
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={invForm.branch_id} onChange={e => setInvForm(f=>({...f,branch_id:e.target.value}))}>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Linked GRN</label>
                <select className="form-control" value={invForm.grn_id} onChange={e => { const g = invGrns.find((g:any) => String(g.id) === e.target.value); setInvForm(f=>({...f,grn_id:e.target.value,invoice_amount:g ? String(Number(g.items_total||0)) : f.invoice_amount, contact_id:g ? String(g.contact_id||f.contact_id) : f.contact_id, branch_id:g ? String(g.branch_id||f.branch_id) : f.branch_id})); }}>
                  <option value="">No GRN link</option>
                  {invGrns.filter((g:any) => g.status==='Received' && (!invForm.contact_id || String(g.contact_id) === String(invForm.contact_id))).map((g:any) => <option key={g.id} value={g.id}>{g.grn_no} --- {g.supplier_name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid-4">
              <div className="form-group"><label className="form-label">Supplier Invoice No</label><input className="form-control" value={invForm.supplier_invoice_no} onChange={e => setInvForm(f=>({...f,supplier_invoice_no:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Invoice Date *</label><input className="form-control" type="date" value={invForm.invoice_date} onChange={e => setInvForm(f=>({...f,invoice_date:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Due Date</label><input className="form-control" type="date" value={invForm.due_date} onChange={e => setInvForm(f=>({...f,due_date:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Amount</label><input className="form-control mono" type="number" value={invForm.invoice_amount} placeholder="Select GRN first" /></div>
            </div>
            <button className="btn btn-primary" onClick={handleInvoice} disabled={saving}>{saving ? 'Posting...' : 'Post Invoice'}</button>
          </div>
        </div>
      )}

      {showPayForm && tab==='payments' && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Pay Supplier</span><button className="btn btn-secondary btn-sm" onClick={() => setShowPayForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Supplier *</label>
                <ContactPicker value={payForm.contact_id} placeholder="Type to search supplier" showBalance onSelect={(c:any) => setPayForm(f=>({...f,contact_id:String(c.id)}))} />
              </div>
            </div>
            <div className="form-grid-3" style={{marginBottom:8}}>
              <div className="form-group"><label className="form-label">Date *</label><input type="date" className="form-control" value={payForm.payment_date} max={new Date().toISOString().split('T')[0]} onChange={e => setPayForm(f=>({...f,payment_date:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Branch *</label><select className="form-control" value={payForm.branch_id} onChange={e => setPayForm(f=>({...f,branch_id:e.target.value}))}><option value="">Select branch</option>{branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Reference</label><input className="form-control" value={payForm.payment_ref} onChange={e => setPayForm(f=>({...f,payment_ref:e.target.value}))} placeholder="Transfer ref..." /></div>
            </div>
            <div style={{marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}><span style={{fontSize:12,fontWeight:600}}>Payment Splits</span><button className="btn btn-secondary btn-sm" type="button" onClick={addPaySplit}>+ Add Account</button></div>
              {paySplits.map((sp,i) => (
                <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 120px auto',gap:8,marginBottom:6}}>
                  <select className="form-control" value={sp.account_id} onChange={e => updatePaySplit(i,'account_id',e.target.value)}><option value="">Select account</option>{cashAccounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select>
                  <input className="form-control mono" type="number" value={sp.amount} onChange={e => updatePaySplit(i,'amount',e.target.value)} placeholder="0.00" />
                  <button className="btn btn-secondary btn-sm" type="button" onClick={() => removePaySplit(i)} disabled={paySplits.length===1}>x</button>
                </div>
              ))}
              <div style={{textAlign:'right',fontWeight:700,color:'var(--red)',fontSize:13}}>Total: {payTotal.toFixed(2)}</div>
            </div>
            <button className="btn btn-success" type="button" onClick={handlePayment} disabled={saving}>{saving ? 'Posting...' : 'Post Payment'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder={`Search ${tab}...`} value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} records</span>
      </div>

      {tab === 'grns' && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>GRN No</th><th>Supplier</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
              : grns.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No GRNs yet.</p></div></td></tr>
              : grns.map(g => (
                <tr key={g.id} onClick={() => setSelectedGrn(g.id)} style={{cursor:'pointer'}}>
                  <td className="td-code">{g.grn_no}</td>
                  <td style={{fontWeight:600}}>{g.supplier_name}</td>
                  <td><span className={`badge ${colorMap[g.status_color]||'badge-gray'}`}>{g.status}</span></td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(g.grn_date || g.created_at).toLocaleDateString()}</td>
                  <td>{g.status==='Draft' && <button className="btn btn-success btn-sm" onClick={e => handleConfirm(g.id,g.grn_no,e)}><CheckCircle size={12}/> Confirm</button>}
                      {g.status!=='Draft' && <span style={{color:'var(--tx3)',fontSize:11}}>Confirmed</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'invoices' && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Invoice No</th><th>Supplier Ref</th><th>Supplier</th><th style={{textAlign:'right'}}>Amount</th><th>Date</th><th>Due</th><th>Status</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
              : invoices.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><p>No invoices yet.</p></div></td></tr>
              : invoices.map(i => (
                <tr key={i.id} onClick={() => setSelectedInvoice(i.id)} style={{cursor:'pointer'}}>
                  <td className="td-code">{i.invoice_no}</td>
                  <td style={{color:'var(--tx3)'}}>{i.supplier_invoice_no || '—'}</td>
                  <td style={{fontWeight:600}}>{i.supplier_name}</td>
                  <td className="td-num td-red">{Number(i.invoice_amount).toFixed(2)}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(i.invoice_date).toLocaleDateString()}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{i.due_date ? new Date(i.due_date).toLocaleDateString() : '—'}</td>
                  <td><span className={`badge ${colorMap[i.status_color]||'badge-gray'}`}>{i.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payments' && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Supplier</th><th>Reference</th><th style={{textAlign:'right'}}>Amount</th><th>Date</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
              : payments.length === 0 ? <tr><td colSpan={4}><div className="empty-state"><p>No payments yet.</p></div></td></tr>
              : payments.map((p:any) => (
                <tr key={p.id}>
                  <td style={{fontWeight:600}}>{p.supplier_name}</td>
                  <td style={{color:'var(--tx3)'}}>{p.payment_ref || '—'}</td>
                  <td className="td-num td-red">{Number(p.amount).toFixed(2)}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(p.paid_at).toLocaleDateString()}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => setSelectedPayment(p.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Purchase;